import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import type { MapStoreBase } from '../..';

/** Trailing delay that keeps only the last of a burst of selections and lands after the sidebar takes focus. */
export const ANNOUNCE_DELAY_MS = 500;

interface Props {
  store: MapStoreBase;
}

/**
 * Polite live region that speaks the result of a map selection (WCAG 4.1.3).
 *
 * Always mounted and initially empty so the first update is announced. Each announcement clears the
 * region first and differs from the previous one by a trailing non-breaking space, so identical
 * consecutive text is spoken again. An announcement already in the store when the region mounts is
 * not replayed.
 */
export default function MapSelectionStatus({ store }: Props) {
  const { selectionAnnouncement } = useSnapshot(store);
  const [text, setText] = useState('');
  const mountedWithIdRef = useRef(store.selectionAnnouncement?.id);

  useEffect(() => {
    if (!selectionAnnouncement || selectionAnnouncement.id === mountedWithIdRef.current) return;
    setText('');
    // VoiceOver skips a message identical to its last one, so every other announcement ends in a
    // non-breaking space, which screen readers don't voice.
    const suffix = selectionAnnouncement.id % 2 === 0 ? '\u00A0' : '';
    const timer = setTimeout(() => setText(`${selectionAnnouncement.text}${suffix}`), ANNOUNCE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [selectionAnnouncement]);

  return (
    <output aria-live="polite" aria-atomic="true" className="sr-only" data-testid="map-selection-status">
      {text}
    </output>
  );
}
